import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

import type { SchedulerViolation } from '../hooks/useSchedulerGeneration'

dayjs.extend(isoWeek)

/**
 * Pure, view-agnostic helper for the inline (per-game) violation display.
 *
 * Kept free of DOM and team lookups so it unit-tests in isolation.
 */

export type ViolationSeverity = 'error' | 'warning' | 'info'

/**
 * The class of a violation under the SINGLE shared count policy. Every count
 * the Review screen shows — inline badges, the constraint panel header, the
 * summary cards, the honest score — must route through {@link classifyViolation}
 * so they never disagree.
 *
 * - `conflict`  — severity 'error', not prevented. Caps the score, renders inline.
 * - `advisory`  — severity 'warning', not prevented. Caps the score, panel-only.
 * - `info`      — severity 'info', not prevented. Shown separately; NEVER caps.
 * - `prevented` — auto-resolved by the solver (`is_prevented`). Excluded from
 *                 every count + inline; the panel may list it muted.
 */
export type ViolationClass = 'conflict' | 'advisory' | 'info' | 'prevented'

/**
 * Classify a violation from its severity + prevented flag. Takes primitives
 * (not a violation object) so both the API shape (`is_prevented`) and the
 * mapped inline shape (`isPrevented`) share one source of truth.
 */
export function classifyViolation(
  severity: ViolationSeverity,
  isPrevented: boolean
): ViolationClass {
  if (isPrevented) {
    return 'prevented'
  }
  if (severity === 'error') {
    return 'conflict'
  }
  if (severity === 'warning') {
    return 'advisory'
  }
  return 'info'
}

export interface ViolationCounts {
  conflicts: number
  advisories: number
  info: number
  prevented: number
}

/**
 * Count violations by class under the shared policy. Operates on the raw API
 * shape (`severity` + `is_prevented`); callers should `dedupeViolations` first
 * if they want the honest (non-double-emitted) total.
 */
export function countViolations(
  violations: Array<{ severity: ViolationSeverity; is_prevented?: boolean }>
): ViolationCounts {
  const counts: ViolationCounts = {
    conflicts: 0,
    advisories: 0,
    info: 0,
    prevented: 0,
  }
  for (const v of violations) {
    switch (classifyViolation(v.severity, v.is_prevented ?? false)) {
      case 'conflict':
        counts.conflicts++
        break
      case 'advisory':
        counts.advisories++
        break
      case 'info':
        counts.info++
        break
      case 'prevented':
        counts.prevented++
        break
    }
  }
  return counts
}

const codeOf = (v: SchedulerViolation): string =>
  v.constraint_type?.code ?? 'unknown'

const sortedNums = (xs: number[] | null | undefined): number[] =>
  [...(xs ?? [])].sort((a, b) => a - b)

const sortedStrs = (xs: string[] | null | undefined): string[] =>
  [...(xs ?? [])].sort()

/**
 * Remove only EXACT duplicate violations — same constraint code, severity,
 * prevented flag, message, affected game numbers AND affected team UIDs.
 *
 * Team identity is part of the key, so this deliberately does NOT collapse two
 * different teams that share a message + game set (e.g. a flight where every
 * team is over its weekly target keeps one advisory per team). It is a
 * defensive guard against the per-constraint double-emit paths in the solver
 * so the inline count and expansion stay honest, NOT a way to shrink the count.
 */
export function dedupeViolations(
  violations: SchedulerViolation[]
): SchedulerViolation[] {
  const seen = new Set<string>()
  const result: SchedulerViolation[] = []
  for (const v of violations) {
    const key = [
      codeOf(v),
      v.severity,
      v.is_prevented ? '1' : '0',
      v.message,
      sortedNums(v.affected_game_numbers).join(','),
      sortedStrs(v.affected_team_uids).join(','),
    ].join('|')
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    result.push(v)
  }
  return result
}

// ── Inline-display helpers (pure view-model, no DOM) ──────────────────────────

/** Resolve affected team UIDs to a readable label; null when none resolve. */
export function resolveTeamLabel(
  teamUids: string[],
  teamNames: Record<string, string>
): string | null {
  const names = teamUids.map((uid) => teamNames[uid]).filter(Boolean)
  return names.length > 0 ? names.join(', ') : null
}

/**
 * Make a per-game advisory read naturally by naming the team in place of the
 * solver's generic leading "Team " (e.g. "Team has too many games…" →
 * "Riverside U12 has too many games…"). When the team can't be resolved the
 * raw message is returned unchanged.
 */
export function intuitiveMessage(
  message: string,
  teamLabel: string | null
): string {
  if (!teamLabel) {
    return message
  }
  if (/^Team\b/.test(message)) {
    return `${teamLabel}${message.slice('Team'.length)}`
  }
  return `${teamLabel} — ${message}`
}

/**
 * Rewrite the solver's terse ISO week stamps ("2027w22") into a plain-language
 * week, e.g. "the week of May 31, 2027" (the Monday that starts ISO week 22 of
 * 2027). Consumes a preceding "week " so "…in week 2027w22" reads
 * "…in the week of May 31, 2027" rather than "…in week the week of …".
 *
 * ISO week 1 always contains Jan 4, so the Monday of week W is Jan 4's Monday
 * plus (W − 1) weeks — correct even across year boundaries (2026w1 → Dec 2025).
 */
export function humanizeWeekRefs(message: string): string {
  if (!message) {
    return message
  }
  return message.replace(
    /(?:\bweek\s+)?\b(\d{4})w(\d{1,2})\b/gi,
    (full: string, year: string, week: string) => {
      const start = dayjs(`${year}-01-04`)
        .startOf('isoWeek')
        .add(Number(week) - 1, 'week')
      return start.isValid()
        ? `the week of ${start.format('MMM D, YYYY')}`
        : full
    }
  )
}

/** Friendlier severity label for the inline expansion. */
export function severityLabel(severity: ViolationSeverity): string {
  if (severity === 'error') {
    return 'Conflict'
  }
  if (severity === 'info') {
    return 'Info'
  }
  return 'Violation'
}

// ── Outcome semantics (constraint-semantics refactor) ─────────────────────────

/**
 * Outcomes that mean "the target was unreachable because of the setup, not a
 * scheduler failure". These never count as conflicts/advisories — they explain
 * a shortfall, so a clean strict win with one of these is "Limited by setup",
 * not "All hard rules satisfied" (invariant 4).
 */
const SETUP_LIMITED_OUTCOMES = new Set([
  'forced_shortfall',
  'setup_impossible',
  'infeasible_configuration',
])

/** Read `context.outcome` as a string, or null (historical / skew rows). */
export function readOutcome(
  context: Record<string, unknown> | null | undefined
): string | null {
  const outcome = context?.outcome
  return typeof outcome === 'string' && outcome.length > 0 ? outcome : null
}

/** Whether an outcome is a setup limitation (forced shortfall / impossible). */
export function isSetupLimitedOutcome(outcome: string | null): boolean {
  return outcome != null && SETUP_LIMITED_OUTCOMES.has(outcome)
}

/**
 * Inline label that respects the outcome: a forced shortfall reads "Limited by
 * setup" (not the bare "Info" its INFO severity would give), a setup
 * impossibility reads "Setup impossible". Everything else uses the severity label.
 */
export function outcomeAwareLabel(
  severity: ViolationSeverity,
  outcome: string | null
): string {
  if (outcome === 'forced_shortfall') {
    return 'Limited by setup'
  }
  if (
    outcome === 'setup_impossible' ||
    outcome === 'infeasible_configuration'
  ) {
    return 'Setup impossible'
  }
  return severityLabel(severity)
}

// ── Inline (per-game) conflict map (pure view-model, no DOM) ──────────────────

/**
 * Normalized violation as the games table consumes it. Both inline sources —
 * the stored `SchedulerViolation[]` and the narrower post-edit
 * `EvaluationResult.violations` — map into this shape before the table reads it.
 */
export interface InlineViolation {
  constraintName: string
  message: string
  suggestion: string | null
  severity: ViolationSeverity
  /** `context.outcome`, when present — drives the outcome-aware inline label. */
  outcome: string | null
  affectedGameNumbers: number[]
  teamUids: string[]
  isPrevented: boolean
}

/** Per-game inline entry (the `InlineViolation` minus its game-number fan-out). */
export interface InlineViolationInfo {
  constraintName: string
  message: string
  suggestion: string | null
  severity: ViolationSeverity
  outcome: string | null
  teamUids: string[]
}

/**
 * Group non-prevented violations by affected game number (conflicts +
 * advisories; info + prevented excluded). This is the solver's RAW mapping,
 * which can be over-broad — a games-per-team-per-week advisory for team5 may
 * list a game team5 doesn't even play. ALWAYS pass each game's entries through
 * {@link relatedGameViolations} before display so a row only shows violations
 * that involve its own two teams, and derive the row's marker (conflict vs
 * advisory) from that filtered result.
 */
export function buildInlineGameViolations(
  violations: InlineViolation[]
): Map<number, InlineViolationInfo[]> {
  const map = new Map<number, InlineViolationInfo[]>()
  for (const v of violations) {
    const cls = classifyViolation(v.severity, v.isPrevented)
    if (cls !== 'conflict' && cls !== 'advisory') {
      continue // info + prevented never mark a row
    }
    for (const gn of v.affectedGameNumbers) {
      const existing = map.get(gn) ?? []
      existing.push({
        constraintName: v.constraintName,
        message: v.message,
        suggestion: v.suggestion,
        severity: v.severity,
        outcome: v.outcome,
        teamUids: v.teamUids,
      })
      map.set(gn, existing)
    }
  }
  return map
}

/**
 * Keep only the per-game violation entries that actually involve one of the
 * game's two teams (or that name no team at all). The solver's
 * affected_game_numbers is over-broad — without this filter a "team4 vs team1"
 * row surfaces advisories about unrelated teams (team5/team8) that merely share
 * the same week.
 */
export function relatedGameViolations(
  entries: InlineViolationInfo[],
  homeTeamId?: string,
  awayTeamId?: string
): InlineViolationInfo[] {
  return entries.filter(
    (e) =>
      e.teamUids.length === 0 ||
      (homeTeamId != null && e.teamUids.includes(homeTeamId)) ||
      (awayTeamId != null && e.teamUids.includes(awayTeamId))
  )
}
